import { CameraConfigurationRequester, GraphQLCameraConfigurationRequester } from '@cobliteam/api-lib';

import type {
  CameraConfiguration,
  FleetCameraEventsConfigurationInput,
  FleetEventsConfiguration,
  PageableContent,
  Sort,
  UpdateCameraConfigurationRequest,
  UpdateCameraSpeedLimit,
  WifiSettings,
  WifiSettingsList,
} from '@cobliteam/common-types';
import Environment from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useUser from '../../useEntity/useUser';

const CONFIGURATION_QUERY_KEY = 'camera-configuration';
const WIFI_QUERY_KEY = 'wifi-settings';

const useCameraConfiguration = () => {
  const env = Environment.getInstance().getEnvironment();
  const cameraConfigurationRequester = new CameraConfigurationRequester({
    host: env.apiHost,
  });
  const cameraConfigurationGraphQLRequester = new GraphQLCameraConfigurationRequester();

  const queryClient = useQueryClient();

  const user = useUser().getUser();

  const getCameraConfiguration = (page?: number, size?: number, sort?: Sort) => {
    return useQuery<PageableContent<CameraConfiguration>>({
      queryKey: [CONFIGURATION_QUERY_KEY, 'page'],
      queryFn: () => cameraConfigurationGraphQLRequester.getCameraConfiguration(page, size, sort),
    });
  };

  const getFleetCameraConfiguration = () => {
    return useQuery<FleetEventsConfiguration>({
      queryKey: [CONFIGURATION_QUERY_KEY, 'fleet'],
      queryFn: () => cameraConfigurationGraphQLRequester.getFleetCameraConfiguration(),
    });
  };

  const optimisticallyUpdateFleetCameraConfigurationCache = async (request: { updateFleetCameraConfigurationRequest: FleetCameraEventsConfigurationInput }) => {
    const previousData = queryClient.getQueriesData({
      queryKey: [CONFIGURATION_QUERY_KEY, 'fleet'],
    });
    const { updateFleetCameraConfigurationRequest } = request;
    queryClient.setQueriesData(
      {
        queryKey: [CONFIGURATION_QUERY_KEY, 'fleet'],
      },
      (oldData: FleetEventsConfiguration | undefined) => {
        if (!oldData?.cameraEventsConfig?.entireFleetCameraEventsConfiguration) return oldData;
        return {
          ...oldData,
          updatedById: user?.id,
          updatedByName: user?.name,
          updatedAt: new Date().toISOString(),
          cameraEventsConfig: {
            entireFleetCameraEventsConfiguration: {
              ...oldData.cameraEventsConfig.entireFleetCameraEventsConfiguration,
              ...updateFleetCameraConfigurationRequest.eventsConfiguration,
            },
          },
        };
      }
    );

    return { previousData };
  };

  const updateFleetCameraConfiguration = useMutation<
    boolean,
    unknown,
    {
      updateFleetCameraConfigurationRequest: FleetCameraEventsConfigurationInput;
    }
  >({
    mutationFn: (request) => {
      return cameraConfigurationGraphQLRequester.updateFleetCameraConfiguration(request.updateFleetCameraConfigurationRequest);
    },
    onMutate: optimisticallyUpdateFleetCameraConfigurationCache,
    onSuccess: () => {
      Alert.success('Parâmetros dos eventos alterados com sucesso');
    },
    onError: (error, variables, context: any) => {
      Alert.error('Ops! Houve um problema ao atualizar os parâmetros dos eventos. Por favor, tente novamente mais tarde');
    },
  });

  // Same request/cache behavior as updateFleetCameraConfiguration, but without the built-in
  // Alert.success/error — callers (e.g. the face-id audio alert save flow) drive their own
  // toast via .then()/.catch(), like useCameraAssociation.updateCameraAssociation does.
  const updateFleetEventsConfiguration = useMutation<
    boolean,
    unknown,
    {
      updateFleetCameraConfigurationRequest: FleetCameraEventsConfigurationInput;
    }
  >({
    mutationFn: (request) => {
      return cameraConfigurationGraphQLRequester.updateFleetCameraConfiguration(request.updateFleetCameraConfigurationRequest);
    },
    onMutate: optimisticallyUpdateFleetCameraConfigurationCache,
  });

  const updateCameraConfiguration = useMutation<boolean, unknown, { updateCameraConfigurationRequest: UpdateCameraConfigurationRequest[] }>({
    mutationFn: (request) => cameraConfigurationGraphQLRequester.updateCameraConfiguration(request.updateCameraConfigurationRequest),
    onMutate: async (request) => {
      const previousData = queryClient.getQueriesData({
        queryKey: [CONFIGURATION_QUERY_KEY, 'page'],
      });
      const { updateCameraConfigurationRequest } = request;

      queryClient.setQueriesData(
        {
          queryKey: [CONFIGURATION_QUERY_KEY, 'page'],
        },
        (oldData: PageableContent<CameraConfiguration> | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            content: oldData.content.map((cameraConfiguration) => {
              const updatedConfiguration = updateCameraConfigurationRequest.find(
                (updatedConfigurationRequest) => updatedConfigurationRequest.deviceId === cameraConfiguration.deviceId
              );
              if (updatedConfiguration) {
                return {
                  ...cameraConfiguration,
                  ...updatedConfiguration,
                };
              }
              return cameraConfiguration;
            }),
          };
        }
      );

      return { previousData };
    },
    onSuccess: () => {
      Alert.success('Configurações de áudio alteradas com sucesso');
    },
    onError: (error, variables, context: any) => {
      Alert.error('Ops! Não foi possível salvar as configurações de alerta sonoro para Cobli Cam. Tente novamente.');
    },
  });

  const updateCameraSpeedLimitConfiguration = useMutation<boolean, unknown, { updateCameraSpeedLimitRequest: UpdateCameraSpeedLimit }>({
    mutationFn: (request) => cameraConfigurationGraphQLRequester.updateCameraSpeedLimit(request.updateCameraSpeedLimitRequest),
  });

  const createWifiSettings = useMutation({
    mutationFn: cameraConfigurationRequester.createWifiSettings,
    onSuccess: async (data, variables, context) => {
      const previousData = queryClient.getQueriesData({
        queryKey: [WIFI_QUERY_KEY],
      });
      queryClient.setQueriesData(
        {
          queryKey: [WIFI_QUERY_KEY],
        },
        (oldData: WifiSettingsList | undefined) => {
          if (!oldData) return oldData;

          const newWifi = {
            id: data.id,
            ssid: data.ssid,
            createdBy: data.createdBy,
            createdAt: data.createdAt,
          } as WifiSettings;
          return {
            ...oldData,
            accessPoints: [...oldData.accessPoints, newWifi],
          };
        }
      );
    },
  });

  const editWifiSettings = useMutation({
    mutationFn: cameraConfigurationRequester.editWifiSettings,
  });

  const deleteWifiSettings = useMutation({
    mutationFn: cameraConfigurationRequester.deleteWifiSettings,
    onMutate: async (id) => {
      queryClient.getQueriesData({
        queryKey: [WIFI_QUERY_KEY],
      });
      queryClient.setQueriesData(
        {
          queryKey: [WIFI_QUERY_KEY],
        },
        (oldData: WifiSettingsList | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            accessPoints: oldData.accessPoints.filter((wifi) => wifi.id !== id),
          };
        }
      );
    },
  });

  const getWifiSettings = () => {
    return useQuery<WifiSettingsList>({
      queryKey: [WIFI_QUERY_KEY],
      queryFn: () => cameraConfigurationGraphQLRequester.getWifiSettings(),
      retry: false,
      enabled: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 300000,
    });
  };

  return {
    getCameraConfiguration,
    getFleetCameraConfiguration,
    updateFleetCameraConfiguration,
    updateFleetEventsConfiguration,
    updateCameraConfiguration,
    updateCameraSpeedLimitConfiguration,
    getWifiSettings,
    createWifiSettings,
    editWifiSettings,
    deleteWifiSettings,
  };
};

export default useCameraConfiguration;
